import { useEffect, useState } from 'react';
import { Translate, useTranslate } from '~/i18n';
import { CloseIcon } from '~/svgs';
import Modal from '~/components/common/modal';
import { Section } from '~/components/common/section';
import { SubmitLoader } from '~/components/common/loaders/submit';
import { TutorialProps } from '~/interfaces/tutorial';
import TutorialCard from './tutorial-card';
import useHelpCenterVideos from '~/apis/dashboard-statistics/useHelpCenterVideos';
import { BackArrowIcon } from '~/svgs/marketplace';
import getIDfromYoutubeUrl from '~/utils/get-id-from-url';

type QuickstartGuideModalProps = {
   closeModal: () => void;
   open: boolean;
   tutorialData?: TutorialProps;
};

const QuickstartGuideModal = ({ closeModal, open, tutorialData }: QuickstartGuideModalProps) => {
   const { lang } = useTranslate();
   const [showVideo, setShowVideo] = useState(false);
   const [selectedTutorial, setSelectedTutorial] = useState<TutorialProps | undefined>(
      tutorialData || undefined
   );
   const { data, isLoading } = useHelpCenterVideos();

   useEffect(() => {
      if (selectedTutorial?._id) setShowVideo(true);
   }, [selectedTutorial]);

   const closeVideo = () => {
      setShowVideo(false);
      setSelectedTutorial(undefined);
      closeModal();
   };

   const tutorials: any = data?.data || {};

   return (
      <>
         <Modal size="lg" open={open} onClickOutside={() => closeVideo()}>
            <Modal.Body>
               <Section className="relative py-5">
                  <div
                     onClick={() => closeVideo()}
                     className="absolute z-10 cursor-pointer top-6 ltr:right-8 rtl:left-8">
                     <CloseIcon />
                  </div>
                  <div className="flex flex-col px-10 pb-5">
                     <span className="mb-2 text-xl font-bold">
                        <Translate
                           id={`tutorial:${showVideo ? 'guide center' : 'quickstart guide'}`}
                        />
                     </span>
                     {showVideo ? null : (
                        <span className="w-full text-xs text-grey-300 md:w-3/4">
                           <Translate id="tutorial:quickstart guide descsription" />
                        </span>
                     )}
                  </div>

                  <hr />

                  {showVideo ? (
                     <div className="flex flex-col gap-3 px-8 py-4">
                        <span
                           className="ltr:mr-3 rtl:ml-3 "
                           onClick={() => {
                              setShowVideo(false);
                              setSelectedTutorial(undefined);
                           }}>
                           <BackArrowIcon
                              width={22}
                              height={22}
                              className="rtl:rotate-180 [&>*]:fill-black cursor-pointer"
                           />
                        </span>

                        <span className="font-bold capitalize">{selectedTutorial?.title}</span>
                        {selectedTutorial?.description ? (
                           <span className="pb-3 text-xs">{selectedTutorial?.description}</span>
                        ) : null}

                        {getIDfromYoutubeUrl(selectedTutorial?.url)?.length ? (
                           <iframe
                              className="w-full rounded"
                              height="390"
                              width="640"
                              src={`https://www.youtube.com/embed/${getIDfromYoutubeUrl(
                                 selectedTutorial?.url
                              )}?controls=0`}
                              title="YouTube video player"
                              allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture;"
                              allowFullScreen></iframe>
                        ) : null}
                     </div>
                  ) : (
                     <>
                        {isLoading ? (
                           <div className="flex justify-center h-56">
                              <SubmitLoader />
                           </div>
                        ) : Object.keys(tutorials)?.length ? (
                           <div className="grid grid-cols-3 gap-4 px-8 py-4">
                              {Object.keys(tutorials)?.map((tutorial_key: any, index: number) => {
                                 const tutorial = tutorials[tutorial_key];
                                 return (
                                    <div key={index}>
                                       <TutorialCard
                                          tutorial={tutorial}
                                          playVideo={(tutorial: any) => {
                                             setSelectedTutorial(tutorial);
                                          }}
                                       />
                                    </div>
                                 );
                              })}
                           </div>
                        ) : (
                           <div className="flex items-center justify-center gap-2 py-10 !text-[14px] text-grey-300 h-52">
                              <Translate id="common:no results" />
                           </div>
                        )}

                        <hr />

                        <div className="flex justify-center mt-5">
                           <a
                              onClick={() => closeVideo()}
                              href={`https://sta.gov.sa/${lang}/contact-us/`}
                              target="_blank"
                              rel="noreferrer"
                              className="text-sm text-primary ltr:text-right rtl:text-left font-semibold">
                              <Translate id="tutorial:need further assistance" />
                           </a>
                        </div>
                     </>
                  )}
               </Section>
            </Modal.Body>
         </Modal>
      </>
   );
};

export default QuickstartGuideModal;
